/**
 * ImageLightbox.jsx - Modal per visualizzare immagini con hotspots
 * ================================================================
 * Apre immagine in fullscreen con hotspots interattivi
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, ChevronLeft, ChevronRight, Plus, ZoomIn, ZoomOut } from 'lucide-react';

const ImageLightbox = ({ 
  isOpen, 
  onClose, 
  images = [], // Array of { url, alt, hotspots: [] }
  initialIndex = 0 
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [activeHotspot, setActiveHotspot] = useState(null);
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    setCurrentIndex(initialIndex);
    setZoom(1);
    setActiveHotspot(null);
  }, [initialIndex, isOpen]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;
    
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') goToPrev();
      if (e.key === 'ArrowRight') goToNext();
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentIndex]);

  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  if (!isOpen || images.length === 0) return null;

  const currentImage = images[currentIndex];
  const hasMultiple = images.length > 1;

  const goToPrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    setActiveHotspot(null);
    setZoom(1);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    setActiveHotspot(null);
    setZoom(1);
  };

  const toggleZoom = () => {
    setZoom(zoom === 1 ? 1.5 : 1);
  };

  return (
    <div 
      className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
      onClick={onClose}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-20 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
      >
        <X size={24} />
      </button>

      {/* Zoom button */}
      <button
        onClick={(e) => { e.stopPropagation(); toggleZoom(); }}
        className="absolute top-4 right-20 z-20 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
      >
        {zoom === 1 ? <ZoomIn size={20} /> : <ZoomOut size={20} />}
      </button>

      {/* Counter */}
      {hasMultiple && (
        <div className="absolute top-4 left-4 z-20 px-4 py-2 rounded-full bg-white/10 text-white text-sm">
          {currentIndex + 1} / {images.length}
        </div>
      )}

      {/* Navigation arrows */}
      {hasMultiple && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); goToPrev(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
          >
            <ChevronLeft size={28} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); goToNext(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
          >
            <ChevronRight size={28} />
          </button>
        </>
      )}

      {/* Image container */}
      <div 
        className="relative max-w-[90vw] max-h-[85vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div 
          className="relative transition-transform duration-300"
          style={{ transform: `scale(${zoom})` }}
        >
          <img
            src={currentImage.url}
            alt={currentImage.alt || ''}
            className="max-w-[90vw] max-h-[85vh] object-contain"
            draggable={false}
          />

          {/* Hotspots */}
          {currentImage.hotspots?.map((hotspot, idx) => (
            <button
              key={hotspot.id || idx}
              className="absolute w-9 h-9 -translate-x-1/2 -translate-y-1/2 z-10 group"
              style={{ 
                left: `${hotspot.position_x}%`, 
                top: `${hotspot.position_y}%`,
                transform: `translate(-50%, -50%) scale(${1/zoom})`
              }}
              onClick={(e) => {
                e.stopPropagation();
                setActiveHotspot(activeHotspot === hotspot.id ? null : hotspot.id);
              }}
            >
              {/* Pulse */}
              <div className="absolute inset-0 rounded-full border-2 border-[#B08B5C]/50 animate-ping" />
              
              {/* Button */}
              <div className="relative w-9 h-9 rounded-full border-2 border-[#B08B5C] bg-black/50 backdrop-blur-sm flex items-center justify-center hover:bg-[#B08B5C] transition-colors group">
                <Plus className="w-4 h-4 text-[#B08B5C] group-hover:text-black" />
              </div>

              {/* Tooltip */}
              {activeHotspot === hotspot.id && hotspot.material && (
                <div 
                  className="absolute left-12 top-0 w-56 bg-[#121212] border border-white/20 rounded-xl p-3 shadow-2xl z-30"
                  style={{ transform: `scale(${1/zoom})`, transformOrigin: 'left top' }}
                >
                  <Link 
                    to={hotspot.link_url || `/materials/${hotspot.material.id}`}
                    className="flex gap-3 hover:opacity-80 transition-opacity"
                    onClick={onClose}
                  >
                    {hotspot.material.image_url && (
                      <img 
                        src={hotspot.material.image_url} 
                        alt={hotspot.material.name}
                        className="w-14 h-14 rounded-lg object-cover"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium text-sm truncate">
                        {hotspot.material.name || hotspot.label}
                      </p>
                      <p className="text-white/40 text-xs capitalize">
                        {hotspot.material.category}
                      </p>
                      <span className="text-[#B08B5C] text-xs mt-1 inline-block">
                        Scopri →
                      </span>
                    </div>
                  </Link>
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Caption */}
      {currentImage.caption && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 px-6 py-3 rounded-xl bg-black/60 backdrop-blur-sm text-white text-sm max-w-lg text-center">
          {currentImage.caption}
        </div>
      )}

      {/* Thumbnails */}
      {hasMultiple && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex gap-2 p-2 rounded-xl bg-black/60 backdrop-blur-sm">
          {images.map((img, idx) => (
            <button
              key={idx}
              onClick={(e) => { 
                e.stopPropagation(); 
                setCurrentIndex(idx);
                setActiveHotspot(null);
                setZoom(1);
              }}
              className={`w-12 h-12 rounded-lg overflow-hidden border-2 transition-all ${
                idx === currentIndex 
                  ? 'border-[#B08B5C] scale-110' 
                  : 'border-transparent opacity-60 hover:opacity-100'
              }`}
            >
              <img src={img.url} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ImageLightbox;
